import { useCookies } from 'react-cookie';
import { useAtom } from 'jotai';
import { APP_ROUTE } from '../../constants';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import LottieAnimation from '../lottie-animation';
import { sidebarAtom } from '../ui/sidebar';

type ErrorCardPropTypes = {
  errorMessage?: string;
  learnMoreClick?: () => void;
  CTAText?: string;
  CTAAction?: () => void;
};

const NewErrorCard = ({
  errorMessage,
  learnMoreClick,
  CTAText,
  CTAAction,
}: ErrorCardPropTypes): JSX.Element => {
  const handleReload = () => {
    window.location.reload();
  };

  const [, , removeCookie] = useCookies();
  const [, setSidebarOpen] = useAtom(sidebarAtom);
  const signOut = () => {
    removeCookie('accessToken', { path: APP_ROUTE.ROOT });
    localStorage.removeItem('workspace');
    localStorage.removeItem('user');
    localStorage.removeItem('sidebar');
    setSidebarOpen(true);
    window.location.reload();
  };

  return (
    <Card className="flex flex-col items-center p-8 max-w-xl mx-auto text-center space-y-6 bg-background/50 backdrop-blur-sm border-0 shadow-none animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="w-40 h-40 transform transition-transform hover:scale-105">
        <LottieAnimation variant="somethingWentWrongEmoji" loop />
      </div>

      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-foreground animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200">
          Something went wrong
        </h1>

        {errorMessage && (
          <p className="text-muted-foreground text-base animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300">
            {errorMessage}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-4 w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-500 delay-400">
        {learnMoreClick && (
          <Button
            variant="link"
            intent="primary"
            onClick={learnMoreClick}
            className="transition-colors hover:text-primary/80"
          >
            Learn More
          </Button>
        )}

        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <Button
              variant="contain"
              intent="primary"
              onClick={handleReload}
              className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98] group"
            >
              <i className="ti ti-refresh animate-spin-slow group-hover:animate-spin mr-2" />
              Try Reloading
            </Button>
            <p className="text-sm text-muted-foreground">
              This will refresh the page and might fix the issue
            </p>
          </div>

          <div className="space-y-2">
            <Button
              variant="outline"
              intent="secondary"
              onClick={signOut}
              className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98] group border-red-200 hover:border-red-300 hover:bg-red-50/50"
            >
              <i className="ti ti-logout mr-2 text-red-500" />
              <span className="text-red-500 font-medium">Sign Out</span>
            </Button>
            <p className="text-sm text-muted-foreground">
              If reloading doesn&apos;t help, try signing out and back in
            </p>
          </div>

          {CTAText && (
            <div className="space-y-2">
              <Button
                variant="outline"
                intent="secondary"
                onClick={CTAAction}
                className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {CTAText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default NewErrorCard;
